import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import {
  APP_SETTING_BLACK_BOX_100,
  APP_SETTING_CLEAR_BOX_100,
  parseBoxCostSetting,
} from '../lib/garbageBags'

export type JobCompletionPhotoPolicy = 'off' | 'required' | 'camera_required'

export interface GarbageBagBoxCostSettings {
  clearBoxOf100: number
  blackBoxOf100: number
}

interface UseAppSettingsResult {
  loading: boolean
  getSetting: <T = unknown>(key: string, fallback?: T) => T | undefined
  photoPolicy: JobCompletionPhotoPolicy
  setPhotoPolicy: (policy: JobCompletionPhotoPolicy) => Promise<{ ok: boolean; error?: string }>
  garbageBagBoxCosts: GarbageBagBoxCostSettings
  setGarbageBagBoxCosts: (
    costs: GarbageBagBoxCostSettings
  ) => Promise<{ ok: boolean; error?: string }>
  refresh: () => Promise<void>
}

function parsePhotoPolicy(value: unknown): JobCompletionPhotoPolicy {
  // Stored as JSONB string: "required" → postgres returns string, or sometimes quoted
  let raw = value
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw)
      if (typeof parsed === 'string') raw = parsed
    } catch {
      // already plain string
    }
  }
  if (raw === 'required' || raw === 'camera_required') return raw
  return 'off'
}

export function useAppSettings(): UseAppSettingsResult {
  const [settings, setSettings] = useState<Record<string, unknown>>({})
  const [loading, setLoading] = useState(false)

  const refresh = useCallback(async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase.from('app_settings').select('key, value')
      if (error) throw error
      const map: Record<string, unknown> = {}
      for (const row of data || []) {
        map[row.key] = row.value
      }
      setSettings(map)
    } catch (e) {
      console.warn('Failed to load app_settings (migration may be pending):', e)
      setSettings({})
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  const getSetting = useCallback(
    <T = unknown>(key: string, fallback?: T): T | undefined => {
      if (!(key in settings)) return fallback
      return settings[key] as T
    },
    [settings]
  )

  const photoPolicy = parsePhotoPolicy(settings.job_completion_photo_policy)

  const garbageBagBoxCosts: GarbageBagBoxCostSettings = {
    clearBoxOf100: parseBoxCostSetting(settings[APP_SETTING_CLEAR_BOX_100]),
    blackBoxOf100: parseBoxCostSetting(settings[APP_SETTING_BLACK_BOX_100]),
  }

  const setPhotoPolicy = useCallback(
    async (policy: JobCompletionPhotoPolicy) => {
      try {
        const { error } = await supabase.from('app_settings').upsert({
          key: 'job_completion_photo_policy',
          value: policy,
          updated_at: new Date().toISOString(),
        })
        if (error) throw error
        await refresh()
        return { ok: true }
      } catch (e: any) {
        return { ok: false, error: e?.message || 'Failed to save setting.' }
      }
    },
    [refresh]
  )

  const setGarbageBagBoxCosts = useCallback(
    async (costs: GarbageBagBoxCostSettings) => {
      const clear = Number(costs.clearBoxOf100)
      const black = Number(costs.blackBoxOf100)
      if (!Number.isFinite(clear) || clear < 0 || !Number.isFinite(black) || black < 0) {
        return { ok: false, error: 'Box costs must be 0 or greater.' }
      }
      try {
        const now = new Date().toISOString()
        const { error } = await supabase.from('app_settings').upsert([
          {
            key: APP_SETTING_CLEAR_BOX_100,
            value: clear,
            updated_at: now,
          },
          {
            key: APP_SETTING_BLACK_BOX_100,
            value: black,
            updated_at: now,
          },
        ])
        if (error) throw error
        await refresh()
        return { ok: true }
      } catch (e: any) {
        return { ok: false, error: e?.message || 'Failed to save bag costs.' }
      }
    },
    [refresh]
  )

  return {
    loading,
    getSetting,
    photoPolicy,
    setPhotoPolicy,
    garbageBagBoxCosts,
    setGarbageBagBoxCosts,
    refresh,
  }
}
